import { COLOR_ROLE_OPTIONS } from "../../lib/color-role";

/**
 * Field definitions for the atomic primitives. They share an align / width / spacing
 * vocabulary on purpose: the point of a primitive is that it behaves the same wherever you
 * drop it.
 */

const align = {
      type: "radio",
      label: "Align",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
      metadata: { source: "manual" },
  },

  color = {
      type: "select",
      label: "Colour",
      options: [...COLOR_ROLE_OPTIONS],
      metadata: {
        source: "manual",
        description:
          "Auto follows whatever this sits in — light over a photo, page colours elsewhere.",
      },
  },
  spacing = {
      type: "select",
      label: "Vertical Spacing",
      options: [
        { label: "None", value: "none" },
        { label: "Tight", value: "tight" },
        { label: "Normal", value: "normal" },
        { label: "Loose", value: "loose" },
      ],
      metadata: { source: "manual" },
  },
  width = {
      type: "select",
      label: "Width",
      options: [
        { label: "Narrow", value: "narrow" },
        { label: "Normal", value: "normal" },
        { label: "Wide", value: "wide" },
        { label: "Full", value: "full" },
      ],
      metadata: { source: "manual" },
  },

  BadgeBlockFields = {
    text: {
      type: "text",
      label: "Text",
      metadata: { source: "ai", seo: true, key: "primitives.badge.text" },
    },
    icon: {
      type: "text",
      label: "Icon",
      metadata: {
        source: "manual",
        format: "lucide-icon",
        description: "Optional icon name, e.g. shield-check",
      },
    },
    tone: {
      type: "radio",
      label: "Tone",
      options: [
        { label: "Accent", value: "accent" },
        { label: "Neutral", value: "neutral" },
        { label: "On photo", value: "onMedia" },
      ],
      metadata: {
        source: "manual",
        description: "On photo is the translucent treatment for badges sitting over imagery.",
      },
    },
    size: {
      type: "radio",
      label: "Size",
      options: [
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
      ],
      metadata: { source: "manual" },
    },
    align,
    spacing,
  } as const,
  ButtonBlockFields = {
    label: {
      type: "text",
      label: "Button Text",
      metadata: { source: "ai", key: "primitives.button.label" },
    },
    url: {
      type: "text",
      label: "Link URL",
      metadata: { source: "manual", description: "A site path, full URL, or #anchor." },
    },
    variant: {
      type: "radio",
      label: "Style",
      options: [
        { label: "Primary", value: "primary" },
        { label: "Outline", value: "outline" },
        { label: "Ghost", value: "ghost" },
      ],
      metadata: { source: "manual" },
    },
    size: {
      type: "radio",
      label: "Size",
      options: [
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
        { label: "Large", value: "large" },
      ],
      metadata: { source: "manual" },
    },
    align,
    spacing,
  } as const,
  DividerBlockFields = {
    width,
    spacing,
  } as const,
  GroupBlockFields = {
    content: {
      type: "slot",
      label: "Content",
    },
    direction: {
      type: "radio",
      label: "Direction",
      options: [
        { label: "Row", value: "row" },
        { label: "Column", value: "column" },
      ],
      metadata: { source: "manual" },
    },
    gap: {
      type: "select",
      label: "Gap",
      options: [
        { label: "Tight", value: "tight" },
        { label: "Normal", value: "normal" },
        { label: "Loose", value: "loose" },
      ],
      metadata: { source: "manual" },
    },
    wrap: {
      type: "radio",
      label: "Wrap",
      options: [
        { label: "Wrap", value: "yes" },
        { label: "No wrap", value: "no" },
      ],
      metadata: {
        source: "manual",
        description: "Rows wrap onto a second line on narrow screens unless you turn this off.",
      },
    },
    align,
    spacing,
  } as const,
  HeadingBlockFields = {
    text: {
      type: "text",
      label: "Heading",
      metadata: { source: "ai", seo: true, key: "primitives.heading.text" },
    },
    level: {
      type: "radio",
      label: "Heading Level",
      options: [
        { label: "H1", value: "h1" },
        { label: "H2", value: "h2" },
        { label: "H3", value: "h3" },
        { label: "H4", value: "h4" },
      ],
      metadata: {
        source: "manual",
        description:
          "The document outline this heading belongs to. Separate from its size. A page should have exactly one H1 — the Hero already provides it unless its copy is hidden.",
        seo: true,
      },
    },
    size: {
      type: "radio",
      label: "Size",
      options: [
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
        { label: "Large", value: "large" },
      ],
      metadata: { source: "manual" },
    },
    color,
    align,
    width,
    spacing,
  } as const,
  ImageBlockFields = {
    imageUrl: {
      type: "custom",
      label: "Image",
      metadata: { source: "manual", description: "The image to place." },
    },
    alt: {
      type: "text",
      label: "Alt Text",
      metadata: {
        source: "manual",
        seo: true,
        description: "Describe the image for screen readers and search engines.",
      },
    },
    caption: {
      type: "text",
      label: "Caption (optional)",
      metadata: { source: "manual" },
    },
    frame: {
      type: "radio",
      label: "Frame",
      options: [
        { label: "None", value: "none" },
        { label: "Shadow", value: "shadow" },
        { label: "Border", value: "border" },
      ],
      metadata: {
        source: "manual",
        description: "Framing around the image. Choose None for logos and marks.",
      },
    },
    width,
    align,
    spacing,
  } as const,
  SectionBlockFields = {
    content: {
      type: "slot",
      label: "Content",
    },
    background: {
      type: "radio",
      label: "Background",
      options: [
        { label: "Page", value: "default" },
        { label: "Muted", value: "muted" },
        { label: "Brand Tint", value: "primary" },
        { label: "None", value: "none" },
      ],
      metadata: { source: "manual" },
    },
    padding: {
      type: "select",
      label: "Padding",
      options: [
        { label: "None", value: "none" },
        { label: "Tight", value: "tight" },
        { label: "Normal", value: "normal" },
        { label: "Loose", value: "loose" },
      ],
      metadata: { source: "manual" },
    },
    anchorId: {
      type: "text",
      label: "Anchor ID",
      metadata: {
        source: "manual",
        description:
          'Lets links jump here. Set "contact" and any button pointing at #contact lands on this section.',
      },
    },
  } as const,
  SpacerBlockFields = {
    height: {
      type: "select",
      label: "Height",
      options: [
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
        { label: "Large", value: "large" },
        { label: "Extra Large", value: "xlarge" },
      ],
      metadata: { source: "manual" },
    },
  } as const,
  TextBlockFields = {
    content: {
      type: "textarea",
      label: "Text",
      metadata: {
        source: "ai",
        seo: true,
        key: "primitives.text.content",
        description: "Separate paragraphs with a blank line.",
      },
    },
    size: {
      type: "radio",
      label: "Size",
      options: [
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
        { label: "Large", value: "large" },
      ],
      metadata: { source: "manual" },
    },
    color,
    align,
    width,
    spacing,
  } as const;

export {
  HeadingBlockFields,
  TextBlockFields,
  ButtonBlockFields,
  ImageBlockFields,
  SpacerBlockFields,
  DividerBlockFields,
  BadgeBlockFields,
  GroupBlockFields,
  SectionBlockFields,
};
